/** Application-wide toast/feedback context used by all feature screens. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { useUiLocale } from '../app/ui-locale';
import { publicCopy } from '../features/renderer/public-copy';
import { translateManagementText } from '../features/management/management-dom-i18n';

interface Notice { id: number; type: 'success' | 'error' | 'warning' | 'info'; text: string }
interface FeedbackContextValue { notify: (text: string, type?: Notice['type']) => void }
const FeedbackContext = createContext<FeedbackContextValue>({ notify: () => undefined });

export function FeedbackProvider({ children }: PropsWithChildren) {
  const { locale } = useUiLocale();
  const copy = publicCopy(locale);
  const [notices, setNotices] = useState<Notice[]>([]);
  const timersRef = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer != null) window.clearTimeout(timer);
    timersRef.current.delete(id);
    setNotices((current) => current.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback((text: string, type: Notice['type'] = 'info') => {
    const id = Date.now() + Math.random();
    const localizedText = translateManagementText(text, locale);
    setNotices((current) => [...current.slice(-4), { id, type, text: localizedText }]);
    const timer = window.setTimeout(() => dismiss(id), type === 'error' ? 7000 : 4500);
    timersRef.current.set(id, timer);
  }, [dismiss, locale]);

  useEffect(() => () => {
    for (const timer of timersRef.current.values()) window.clearTimeout(timer);
    timersRef.current.clear();
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);
  return <FeedbackContext.Provider value={value}>
    {children}
    <div className="df-toasts" aria-live="polite" aria-atomic="false">
      {notices.map((notice) => <div
        key={notice.id}
        className={`df-toast df-toast--${notice.type}`}
        role={notice.type === 'error' || notice.type === 'warning' ? 'alert' : 'status'}
      >
        <span>{notice.text}</span>
        <button type="button" onClick={() => dismiss(notice.id)} aria-label={copy.closeMessage}>×</button>
      </div>)}
    </div>
  </FeedbackContext.Provider>;
}

export function useFeedback() { return useContext(FeedbackContext); }
